import {HttpErrorResponse, HttpEvent, HttpHandler, HttpInterceptor, HttpRequest} from '@angular/common/http';
import {Injectable, Injector} from '@angular/core';
import {Observable, catchError, from, switchMap, throwError, timeout} from 'rxjs';
import {environment} from '../../../environments/environment';
import {normalizeBaseUrl} from './api-url';
import {ApiCompatibilityBlockedError, ApiCompatibilityService} from './api-compatibility.service';

@Injectable()
export class ApiCompatibilityInterceptor implements HttpInterceptor {
  constructor(private readonly injector: Injector) {}

  intercept(request: HttpRequest<unknown>, next: HttpHandler): Observable<HttpEvent<unknown>> {
    const apiBase = normalizeBaseUrl(environment.apiBaseUrl ?? '');
    const apiPath = apiBase && request.url.startsWith(`${apiBase}/`)
      ? request.url.slice(apiBase.length) : '';
    if (!apiPath) return next.handle(request);
    const transport = () => next.handle(request).pipe(timeout(this.requestTimeout(request, apiPath)));
    if (apiPath.startsWith('/v1/auth/') || apiPath.startsWith('/v1/system/version')) return transport();
    const compatibility = this.injector.get(ApiCompatibilityService);
    const mutation = !/^(GET|HEAD|OPTIONS)$/i.test(request.method);
    const send = () => transport().pipe(catchError((error: unknown) => {
      const watchedFailure = error instanceof HttpErrorResponse
        && (error.status === 404 || error.status >= 500);
      if (!watchedFailure || !compatibility.active()
        || (compatibility.state() !== 'compatible' && compatibility.state() !== 'checking')) {
        return throwError(() => error);
      }
      // A response can expose a server switch after the startup probe. Recheck without replaying it.
      const check = compatibility.recheck();
      return from(check).pipe(switchMap(state => throwError(() => state === 'compatible'
        || !mutation ? error : new ApiCompatibilityBlockedError(state))));
    }));
    return mutation ? from(compatibility.ensureChecked()).pipe(switchMap(state =>
      state === 'compatible' ? send() : throwError(() => new ApiCompatibilityBlockedError(state)))) : send();
  }

  private requestTimeout(request: HttpRequest<unknown>, apiPath: string): number {
    const path = apiPath.split(/[?#]/, 1)[0];
    if (path === '/v1/system/version') return 5000;
    if (request.method === 'POST' && path === '/v1/products/lookup') return 16000;
    if (request.method === 'DELETE' && /^\/v1\/auth\/me(?:\/workspace)?$/.test(path)) return 120000;
    const longOperation = request.method === 'POST' && (
      /^\/v1\/(?:me\/basket\/ai-suggestion|inventory\/bulk-move|qr-code-batches|payments\/checkout-sessions|auth\/google|auth\/me\/demo-data\/create)$/.test(path)
      || /^\/v1\/item-containers\/[^/]+\/(?:items\/ai-suggestion|containers)$/.test(path)
      || /^\/v1\/moderation\/publication-requests\/[^/]+\/seo\/translate$/.test(path)
      || /^\/v1\/admin\/releases\/[^/]+\/notes\/[^/]+\/translate$/.test(path)
      || /^\/v1\/payments\/subscriptions\/[^/]+\/cancel$/.test(path));
    return longOperation || (typeof FormData !== 'undefined' && request.body instanceof FormData) ? 120000 : 15000;
  }
}
